// src/components/SmartDocument/DatabaseEditModal.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  Database,
  Check,
  AlertCircle,
  Search,
  Plus,
  Minus,
  Sparkles,
} from "lucide-react";

const DatabaseEditModal = ({
  currentDatabases,
  allDatabases,
  onClose,
  onSubmit,
}) => {
  const [selectedDatabases, setSelectedDatabases] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    // 현재 연결된 데이터베이스 ID들로 초기화
    const currentIds = currentDatabases.map((db) => db.database_id);
    setSelectedDatabases(currentIds);
  }, [currentDatabases]);

  const filteredDatabases = allDatabases.filter(
    (db) =>
      db.database_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      db.database_description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleDatabase = (databaseId) => {
    setSelectedDatabases((prev) => {
      if (prev.includes(databaseId)) {
        // 최소 1개는 유지
        if (prev.length === 1) {
          setError("최소 1개 이상의 데이터베이스를 선택해야 합니다.");
          return prev;
        }
        setError("");
        return prev.filter((id) => id !== databaseId);
      } else {
        setError("");
        return [...prev, databaseId];
      }
    });
  };

  const handleSubmit = () => {
    if (selectedDatabases.length === 0) {
      setError("최소 1개 이상의 데이터베이스를 선택해야 합니다.");
      return;
    }
    onSubmit(selectedDatabases);
  };

  const isCurrentDatabase = (databaseId) => {
    return currentDatabases.some((db) => db.database_id === databaseId);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
                <Database className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  지식 데이터베이스 관리
                </h2>
                <p className="text-sm text-blue-100 mt-1">
                  문서에 연결할 데이터베이스를 선택하세요
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="데이터베이스 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-600">
                전체: {allDatabases.length}개
              </span>
              <span className="text-blue-600 font-medium">
                선택됨: {selectedDatabases.length}개
              </span>
            </div>
            {error && (
              <div className="flex items-center gap-1 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>

        {/* Database List */}
        <div
          className="p-4 overflow-y-auto"
          style={{ maxHeight: "calc(85vh - 240px)" }}
        >
          <div className="space-y-2">
            {filteredDatabases.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>검색 결과가 없습니다</p>
              </div>
            ) : (
              filteredDatabases.map((db) => {
                const isSelected = selectedDatabases.includes(db.database_id);
                const isCurrent = isCurrentDatabase(db.database_id);

                return (
                  <div
                    key={db.database_id}
                    onClick={() => handleToggleDatabase(db.database_id)}
                    className={`
                      p-4 rounded-xl border-2 cursor-pointer transition-all
                      ${
                        isSelected
                          ? "border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div
                          className={`
                          w-5 h-5 rounded-lg border-2 flex items-center justify-center mt-0.5 transition-all
                          ${
                            isSelected
                              ? "border-blue-500 bg-blue-500"
                              : "border-gray-300 bg-white"
                          }
                        `}
                        >
                          {isSelected && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">
                              {db.database_name}
                            </h3>
                            {isCurrent && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                현재 연결됨
                              </span>
                            )}
                            {db.base_type === "build_typ_s001" && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                공유받음
                              </span>
                            )}
                          </div>
                          {db.database_description && (
                            <p className="text-sm text-gray-600 mt-1">
                              {db.database_description}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Database className="w-3 h-3" />
                              {db.chunk_size || 0} chunks
                            </span>
                            {db.data_size && (
                              <span>
                                {(db.data_size / 1024 / 1024).toFixed(1)} MB
                              </span>
                            )}
                            {db.shared_by && (
                              <span>공유자: {db.shared_by}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="ml-3">
                        {isSelected ? (
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Minus className="w-4 h-4 text-blue-600" />
                          </div>
                        ) : (
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <Plus className="w-4 h-4 text-gray-600" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedDatabases.length === 0 ? (
                <span className="text-red-600">
                  최소 1개 이상의 데이터베이스를 선택해주세요
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span>
                    선택한 {selectedDatabases.length}개의 데이터베이스를
                    기반으로 문서를 편집합니다
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={selectedDatabases.length === 0}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                적용하기
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseEditModal;
